import { sb } from '../supabase';
import { CLARITY_PROJECT } from './growthClarity';

// Growth OS → the exact Clarity recordings. Every session is tagged with our
// session_id (identify's custom session id, growthTracker.tagClarity), and
// Clarity's recordings page takes that filter in its URL — confirmed by the
// owner on 2026-11-01:
//   /projects/view/<project>/impressions?date=Custom&start=<ms>&end=<ms>&CustomSessionId=is%3B<id>
// start/end are whole UTC days; one session per link (a multi-id format is not
// documented, so none is guessed). Clarity keeps recordings 30 days.

const DAY = 864e5;
export const CLARITY_RETENTION_DAYS = 30;

export function recordingUrl(sessionId, atMs = Date.now(), project = CLARITY_PROJECT) {
  if (!project || !sessionId) return null;
  const d = new Date(atMs);
  const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const start = day - DAY;
  const end = day + 2 * DAY - 1;
  return `https://clarity.microsoft.com/projects/view/${project}/impressions?date=Custom&start=${start}&end=${end}&CustomSessionId=${encodeURIComponent(`is;${sessionId}`)}`;
}

// The newest distinct sessions among event rows ({ session_id, occurred_at }).
export function latestSessions(rows, limit = 8) {
  const seen = new Map();
  rows.forEach((r) => {
    if (!r.session_id) return;
    const at = Date.parse(r.occurred_at);
    if (!seen.has(r.session_id) || seen.get(r.session_id) < at) seen.set(r.session_id, at);
  });
  return [...seen.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([session_id, at]) => ({ session_id, at }));
}

// Sessions that sent `event` (optionally with props[key] = value) in [from, to).
export async function sessionsFor(event, { key, value, from, to, limit = 8 } = {}) {
  let q = sb.from('growth_events_clean').select('session_id, occurred_at').eq('event_name', event).not('session_id', 'is', null);
  if (key) q = q.eq(`props->>${key}`, String(value));
  if (from) q = q.gte('occurred_at', new Date(from).toISOString());
  if (to) q = q.lt('occurred_at', new Date(to).toISOString());
  const { data, error } = await q.order('occurred_at', { ascending: false }).limit(300);
  if (error) throw error;
  return latestSessions(data || [], limit);
}

// Searches that found nothing in [from, to), by query, with their sessions.
export async function zeroResultSearches(from, to) {
  const { data, error } = await sb.from('growth_events_clean').select('session_id, occurred_at, props')
    .eq('event_name', 'search_results_shown').eq('props->>zero_results', 'true')
    .gte('occurred_at', new Date(from).toISOString()).lt('occurred_at', new Date(to).toISOString())
    .order('occurred_at', { ascending: false }).limit(2000);
  if (error) throw error;
  const by = {};
  (data || []).forEach((r) => {
    const q = String(r.props?.query || '').trim();
    if (!q) return;
    (by[q] = by[q] || { query: q, n: 0, rows: [] }).n += 1;
    by[q].rows.push(r);
  });
  return Object.values(by).sort((a, b) => b.n - a.n).slice(0, 30).map((g) => ({ query: g.query, n: g.n, sessions: latestSessions(g.rows, 8) }));
}

// ── The exact recording (owner, 2026-11-01) ──────────────────────────────────
// Since the clarity_session_linked event, each of our sessions carries
// Clarity's OWN ids — read in the browser from clarity('metadata'), the same
// values Clarity keeps in its _clck (user) / _clsk (session) cookies. Clarity's
// player opens one recording at
//   https://clarity.microsoft.com/player/<project>/<clarity user>/<clarity session>
// That path is not in Microsoft's documentation; it is the format community
// tools build from the same cookies, and the owner confirmed it opens the
// recording (see the merge commit). Sessions without the ids — older ones, or
// a browser where Clarity did not load — keep the filtered link above.
export function playerUrl(clarityUser, claritySession, project = CLARITY_PROJECT) {
  const ok = (v) => typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v);
  if (!project || !ok(clarityUser) || !ok(claritySession)) return null;
  return `https://clarity.microsoft.com/player/${project}/${clarityUser}/${claritySession}`;
}

// The first Clarity recording of each of our sessions: { [session_id]: { user, session, at } }.
export function clarityIdsBySession(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    const user = r.props?.clarity_user; const session = r.props?.clarity_session;
    if (!r.session_id || !user || !session) return;
    const at = Date.parse(r.occurred_at);
    if (!out[r.session_id] || at < out[r.session_id].at) out[r.session_id] = { user, session, at };
  });
  return out;
}

export async function withClarityIds(sessions) {
  const ids = [...new Set((sessions || []).map((s) => s.session_id).filter(Boolean))];
  if (!ids.length) return sessions || [];
  const { data, error } = await sb.from('growth_events_clean').select('session_id, occurred_at, props')
    .eq('event_name', 'clarity_session_linked').in('session_id', ids).limit(500);
  if (error) throw error;
  const by = clarityIdsBySession(data);
  return sessions.map((s) => (by[s.session_id] ? { ...s, clarity: by[s.session_id] } : s));
}

// The link for one session: the player when we have Clarity's ids, else the filtered list.
export const sessionLink = (s, project = CLARITY_PROJECT) => (s.clarity && playerUrl(s.clarity.user, s.clarity.session, project))
  || recordingUrl(s.session_id, s.at, project);
